import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Upload, FileText, Camera, Info, ShieldAlert, CheckCircle2, X, Scan,
  Loader2, Sparkles, Clock, Lock, HelpCircle, ChevronDown, RotateCcw, Truck,
} from "lucide-react";
import TransporterProfileCard from "../components/TransporterProfileCard";
import { BackButton } from "../components/BackButton";

const DOC_LABELS = {
  id_card: "Pièce d'identité",
  driving_license: "Permis de conduire",
  selfie: "Selfie de vérification",
  vehicle_registration: "Carte grise",
  vehicle_insurance: "Assurance véhicule",
  rc_pro: "Assurance RC Pro",
  cargo_insurance: "Assurance marchandise",
  kbis: "Justificatif d’entreprise (RNE / KBIS)",
  transport_license: "Licence et inscription au registre des transporteurs",
  fimo_fco: "FIMO / FCO",
  technical_inspection: "Contrôle technique",
  vehicle_photo: "Photo véhicule",
};

const DUAL_SIDE = new Set(["id_card", "driving_license"]);
const AI_KYC_PREANALYSIS_ENABLED = process.env.REACT_APP_ENABLE_AI_KYC_PREANALYSIS === "1";

const VEHICLE_LABELS = {
  car: "Voiture", van: "Utilitaire", truck: "Camion", heavy_truck: "Poids lourd",
};

const LICENSE_DETAILS = {
  B1: "Quadricycles lourds (ne couvre pas les voitures)",
  B: "Voiture / Utilitaire ≤ 3,5 t",
  C1E: "Camion 3,5 à 7,5 t + remorque > 750 kg",
  C: "Camion > 3,5 t",
  CE: "Poids lourd avec remorque (FIMO/FCO requis)",
};

const STATUS_BADGE = {
  pending: { color: "bg-amber-500", label: "EN ATTENTE" },
  validated: { color: "bg-emerald-600", label: "VALIDÉ" },
  rejected: { color: "bg-red-600", label: "REFUSÉ" },
};

// Compute SHA-256 hash of a file (for anti-fraud audit trail)
async function hashFile(file) {
  const buf = await file.arrayBuffer();
  const h = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(h)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

function readFileAsDataURL(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

function isCategoryOk(submitted, required) {
  const h = { B: 1, C: 2, CE: 3 };
  return (h[(submitted || "").toUpperCase()] || 0) >= (h[required] || 0);
}

export default function KycCenter() {
  const navigate = useNavigate();
  const [kyc, setKyc] = useState({
    status: "incomplete", required: [], documents: [],
    required_license_category: "B", vehicle_type: "car",
  });
  const [profile, setProfile] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);

  // Drafts: { [docType]: { front?, back?, category?, note? } }
  const [drafts, setDrafts] = useState({});
  const [submittingAll, setSubmittingAll] = useState(false);
  const [submitProgress, setSubmitProgress] = useState({ done: 0, total: 0, current: "" });
  const [infoOpen, setInfoOpen] = useState(false);
  const [savingVehicle, setSavingVehicle] = useState(false);

  const load = async () => {
    try {
      const [kycRes, profRes] = await Promise.all([
        api.get("/transporter/kyc-status"),
        api.get("/transporter/profile").catch(() => ({ data: null })),
      ]);
      setKyc(kycRes.data);
      setProfile(profRes.data);
      // Ouvre automatiquement la section profil si elle n'est pas encore remplie
      if (!profRes.data || !profRes.data.base_city) setProfileOpen(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const updateVehicleType = async (newType) => {
    if (newType === kyc.vehicle_type) return;
    setSavingVehicle(true);
    try {
      await api.patch("/transporter/vehicle-type", { vehicle_type: newType });
      toast.success("Véhicule mis à jour");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingVehicle(false);
    }
  };

  const updateDraft = (type, patch) => {
    setDrafts((prev) => ({ ...prev, [type]: { ...(prev[type] || {}), ...patch } }));
  };

  const clearDraft = (type) => {
    setDrafts((prev) => {
      const n = { ...prev }; delete n[type]; return n;
    });
  };

  const resetDoc = async (docId, docType) => {
    try {
      await api.delete(`/documents/${docId}`);
      // Also clear any local draft for this type
      setDrafts((prev) => {
        const n = { ...prev }; delete n[docType]; return n;
      });
      toast.success("Document réinitialisé — vous pouvez en envoyer un nouveau");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [savingExpiry, setSavingExpiry] = useState("");
  const saveExpiry = async (docType, value) => {
    setSavingExpiry(docType);
    try {
      const { data } = await api.patch(`/documents/${docType}/expiry`, { expires_at: value || null });
      toast.success(data.status === "pending"
        ? "Échéance enregistrée — nouvelle validation humaine requise"
        : value ? "Date d'expiration enregistrée" : "Date d'expiration effacée");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingExpiry(""); }
  };

  const byType = Object.fromEntries(kyc.documents.map((d) => [d.doc_type, d]));

  const PREFERRED_ORDER = [
    "vehicle_registration",
    "vehicle_insurance",
    "rc_pro",
    "cargo_insurance",
    "id_card",
    "driving_license",
    "selfie",
    "vehicle_photo",
    "fimo_fco",
    "technical_inspection",
    "kbis", "transport_license",
  ];
  const presentTypes = new Set([
    ...kyc.required, ...Object.keys(byType),
    "cargo_insurance", "vehicle_photo", "id_card", "driving_license", "selfie",
  ]);
  const allTypes = PREFERRED_ORDER.filter((t) => presentTypes.has(t));

  // Compute which drafts are "ready to submit" (have content + pass basic validation)
  const readyDraftTypes = useMemo(() => {
    return Object.keys(drafts).filter((type) => {
      const d = drafts[type];
      if (!d) return false;
      if (!d.front?.file) return false;
      if (DUAL_SIDE.has(type) && !d.back?.file) return false;
      return true;
    });
  }, [drafts]);

  // Drafts that have content but are INVALID
  const incompleteDraftTypes = useMemo(() => {
    return Object.keys(drafts).filter((type) => {
      const d = drafts[type];
      if (!d) return false;
      const hasContent = d.front?.file || d.back?.file;
      return hasContent && !readyDraftTypes.includes(type);
    });
  }, [drafts, readyDraftTypes]);

  // Liste des documents requis pour ce profil (statut KYC complet)
  // = documents requis backend + documents d'identité toujours obligatoires.
  const requiredTypes = useMemo(() => {
    const set = new Set(kyc.required || []);
    ["selfie", "id_card", "driving_license"].forEach((t) => set.add(t));
    return Array.from(set);
  }, [kyc.required]);

  // Compteur de progression : documents requis qui sont soit déjà soumis (doc présent en base), soit prêts en brouillon
  const progress = useMemo(() => {
    const total = requiredTypes.length;
    const done = requiredTypes.filter((t) => byType[t] || readyDraftTypes.includes(t)).length;
    return { done, total };
  }, [requiredTypes, byType, readyDraftTypes]);

  // Pièces obligatoires encore manquantes (ni déjà soumises, ni prêtes en brouillon)
  const missingRequired = useMemo(() => {
    return requiredTypes.filter((t) => !byType[t] && !readyDraftTypes.includes(t));
  }, [requiredTypes, byType, readyDraftTypes]);

  // Toutes les pièces obligatoires sont fournies -> envoi autorisé
  const allRequiredProvided = missingRequired.length === 0;

  const buildPayload = async (type) => {
    const d = drafts[type] || {};
    const isLicense = type === "driving_license";
    const front = d.front;
    const back = d.back;
    const [frontData, frontHash] = await Promise.all([
      readFileAsDataURL(front.file), hashFile(front.file),
    ]);
    let backData = null, backHash = null;
    if (back?.file) {
      [backData, backHash] = await Promise.all([
        readFileAsDataURL(back.file), hashFile(back.file),
      ]);
    }
    return {
      doc_type: type,
      file_data: frontData,
      file_data_back: backData,
      license_category: isLicense ? (d.category || kyc.required_license_category || "B") : null,
      file_hash: frontHash,
      file_hash_back: backHash,
      file_name: front.file.name,
      file_name_back: back?.file?.name || null,
      file_size: front.file.size,
      file_size_back: back?.file?.size || null,
      file_last_modified: front.file.lastModified ? new Date(front.file.lastModified).toISOString() : null,
      file_last_modified_back: back?.file?.lastModified ? new Date(back.file.lastModified).toISOString() : null,
      captured_from_camera: front.source === "camera",
      captured_from_camera_back: back?.source === "camera",
      note: d.note || null,
    };
  };

  const submitAll = async () => {
    if (!allRequiredProvided) {
      const labels = missingRequired.map((t) => DOC_LABELS[t] || t).join(", ");
      toast.error(`Pièces obligatoires manquantes : ${labels}`);
      return;
    }
    if (readyDraftTypes.length === 0) {
      toast.error("Aucun document prêt à être envoyé");
      return;
    }
    // Validate license category before any upload
    for (const type of readyDraftTypes) {
      if (type === "driving_license") {
        const cat = drafts[type]?.category || kyc.required_license_category || "B";
        if (!isCategoryOk(cat, kyc.required_license_category)) {
          toast.error(`Permis ${kyc.required_license_category} minimum requis (vous avez sélectionné ${cat}).`);
          return;
        }
      }
    }
    // RGPD Art. 9 : consentement explicite avant l'analyse IA des documents biométriques
    const needsBiometricConsent = AI_KYC_PREANALYSIS_ENABLED
      && readyDraftTypes.some((t) => t === "selfie" || t === "id_card");
    if (needsBiometricConsent) {
      try {
        const { data: me } = await api.get("/auth/me");
        if (!me?.ai_biometric_consent_at) {
          const ok = window.confirm(
            "Consentement RGPD Art. 9 — Vérification biométrique par IA\n\n"
            + "Une pré-analyse facultative peut comparer votre pièce d'identité et votre selfie.\n\n"
            + "• Le prestataire, les durées et les garanties sont décrits dans la politique applicable\n"
            + "• Vous pouvez retirer ce consentement à tout moment dans Mon compte → Confidentialité\n"
            + "• Refuser conserve le parcours de contrôle humain\n\n"
            + "Cliquez OK pour donner votre consentement, ou Annuler pour continuer sans pré-analyse."
          );
          if (!ok) {
            toast.info("Pré-analyse refusée — les documents seront contrôlés manuellement");
          } else {
            await api.patch("/users/me/consents", { ai_biometric_consent: true });
            toast.success("Consentement RGPD enregistré");
          }
        }
      } catch (e) {
        toast.error(formatApiError(e));
        return;
      }
    }

    // Order: id_card FIRST so selfie face-match has reference photo, then everything else, selfie LAST.
    const ordered = [...readyDraftTypes].sort((a, b) => {
      const rank = (t) => (t === "id_card" ? 0 : t === "selfie" ? 99 : 50);
      return rank(a) - rank(b);
    });

    setSubmittingAll(true);
    setSubmitProgress({ done: 0, total: ordered.length, current: "" });

    let okCount = 0;
    let failCount = 0;
    for (let i = 0; i < ordered.length; i++) {
      const type = ordered[i];
      setSubmitProgress({ done: i, total: ordered.length, current: DOC_LABELS[type] || type });
      try {
        const payload = await buildPayload(type);
        await api.post("/documents", payload);
        clearDraft(type);
        okCount++;
      } catch (e) {
        failCount++;
        toast.error(`${DOC_LABELS[type] || type} : ${formatApiError(e)}`);
      }
    }
    setSubmitProgress({ done: ordered.length, total: ordered.length, current: "" });
    setSubmittingAll(false);

    if (okCount > 0 && failCount === 0) {
      toast.success(
        `${okCount} document${okCount > 1 ? "s" : ""} envoyé${okCount > 1 ? "s" : ""} ! ` +
        `Vérification IA en cours — quelques minutes.`
      );
      // Recharge pour mettre à jour les statuts, puis redirige vers le dashboard transporteur
      await load();
      setTimeout(() => navigate("/transporter"), 1500);
    } else if (okCount > 0) {
      toast.warning(`${okCount} envoyé${okCount > 1 ? "s" : ""}, ${failCount} échec${failCount > 1 ? "s" : ""}. Corrigez les documents en erreur.`);
      load();
    } else {
      load();
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10 pb-32" data-testid="kyc-center">
      <BackButton to="/transporter" label="Retour au tableau de bord" />
      <div className="overline mb-2">— Centre KYC</div>
      <h1 className="font-display font-black text-4xl tracking-tighter mb-3">Mes documents</h1>
      <p className="text-sm text-neutral-600 mb-6">
        Statut KYC global :{" "}
        <span className={`font-mono ${kyc.status === "validated" ? "text-emerald-700" : kyc.status === "pending" ? "text-amber-600" : "text-red-600"}`}>
          {kyc.status.toUpperCase()}
        </span>
      </p>

      {/* Profil transporteur - section dépliable, ouverte par défaut si pas encore rempli */}
      <div className="border border-[#1F4032]/20 rounded-md mb-4 overflow-hidden bg-white" data-testid="profile-section">
        <button
          type="button"
          onClick={() => setProfileOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-[#1F4032]/5 transition-colors"
          data-testid="profile-section-toggle"
        >
          <div className="flex items-center gap-3">
            <Truck className="w-5 h-5 text-[#1F4032]" />
            <div>
              <div className="font-display font-bold text-base">
                Mon profil transporteur
                {!profile?.base_city && (
                  <span className="ml-2 text-[10px] font-mono px-2 py-0.5 bg-amber-500 text-white rounded">À COMPLÉTER</span>
                )}
                {profile?.base_city && (
                  <span className="ml-2 text-[10px] font-mono px-2 py-0.5 bg-emerald-600 text-white rounded">COMPLET</span>
                )}
              </div>
              <div className="text-xs text-neutral-600 mt-0.5">
                {profile?.base_city
                  ? `Véhicule, zone (${profile.base_city}), rayon (${profile.radius_km} km) — modifiable`
                  : "Renseignez votre véhicule, votre zone d'opération et votre rayon pour recevoir des missions"}
              </div>
            </div>
          </div>
          <ChevronDown className={`w-5 h-5 text-neutral-500 transition-transform ${profileOpen ? "rotate-180" : ""}`} />
        </button>
        {profileOpen && (
          <div className="border-t border-neutral-200">
            <TransporterProfileCard profile={profile} onSaved={load} />
          </div>
        )}
      </div>

      {/* Comment ça marche - encart explicatif */}
      <div className="border border-[#1F4032]/20 rounded-md mb-4 overflow-hidden bg-white" data-testid="how-it-works-card">
        <button
          type="button"
          onClick={() => setInfoOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-[#1F4032]/5 transition-colors"
          data-testid="how-it-works-toggle"
        >
          <div className="flex items-center gap-3">
            <HelpCircle className="w-5 h-5 text-[#1F4032]" />
            <div>
              <div className="font-display font-bold text-base">Comment ça marche ?</div>
              <div className="text-xs text-neutral-600 mt-0.5">Que veut dire « En attente » et où sont stockés vos documents</div>
            </div>
          </div>
          <ChevronDown className={`w-5 h-5 text-neutral-500 transition-transform ${infoOpen ? "rotate-180" : ""}`} />
        </button>
        {infoOpen && (
          <div className="px-4 pb-4 grid md:grid-cols-3 gap-3 text-sm border-t border-neutral-200 pt-4">
            <div className="flex items-start gap-3 p-3 rounded-md bg-amber-50 border border-amber-200">
              <Clock className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-amber-900">« En attente »</div>
                <div className="text-amber-900/80 text-xs mt-1">
                  Une pré-analyse technique peut contrôler le type et la lisibilité des documents. Elle ne valide jamais seule le dossier : la décision reste humaine.
                  Validation par notre équipe après contrôle des justificatifs et de leur validité.
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-md bg-blue-50 border border-blue-200">
              <Lock className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-blue-900">Stockage sécurisé</div>
                <div className="text-blue-900/80 text-xs mt-1">
                  Documents stockés <strong>chiffrés</strong> sur nos serveurs européens (RGPD).
                  Accès limité à l'équipe de conformité MML. Empreinte SHA-256 calculée pour détecter toute altération.
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-md bg-emerald-50 border border-emerald-200">
              <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-emerald-900">Une seule soumission</div>
                <div className="text-emerald-900/80 text-xs mt-1">
                  Préparez tous vos fichiers, puis cliquez <strong>une seule fois</strong> sur
                  « Soumettre tous mes documents » en bas de page. L'ordre est géré automatiquement
                  (pièce d'identité avant le selfie pour la reconnaissance faciale).
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Info permis requis avec sélecteur de véhicule */}
      <div className="flex items-start gap-3 p-4 rounded-md bg-neutral-50 border border-neutral-200 mb-4" data-testid="license-info-banner">
        <Info className="w-5 h-5 text-neutral-600 mt-0.5 shrink-0" />
        <div className="text-sm flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">Votre véhicule :</span>
            <select
              value={kyc.vehicle_type || "car"}
              onChange={(e) => updateVehicleType(e.target.value)}
              disabled={savingVehicle}
              className="text-sm font-semibold border border-neutral-300 rounded px-2 py-1 bg-white hover:border-[#1F4032] focus:outline-none focus:ring-2 focus:ring-[#1F4032]/20"
              data-testid="vehicle-type-select"
            >
              <option value="car">Voiture</option>
              <option value="van">Utilitaire</option>
              <option value="truck">Camion</option>
              <option value="heavy_truck">Poids lourd</option>
            </select>
            <span className="text-neutral-600">— Permis requis :</span>
            <span className="inline-block px-2 py-0.5 bg-[#1F4032] text-white font-mono text-xs rounded">
              {kyc.required_license_category}
            </span>
            {savingVehicle && <Loader2 className="w-3 h-3 animate-spin text-neutral-500" />}
          </div>
          <div className="text-neutral-600 mt-1 text-xs">
            {LICENSE_DETAILS[kyc.required_license_category]} · Recto ET verso obligatoires.
          </div>
        </div>
      </div>

      {/* Anti-fraude banner */}
      <div className="flex items-start gap-3 p-4 rounded-md bg-amber-50 border border-amber-200 mb-8">
        <ShieldAlert className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
        <div className="text-sm">
          <div className="font-semibold text-amber-900">Fichiers non modifiés exigés</div>
          <div className="text-amber-900/80 mt-1">
            Privilégiez la prise de photo directe via votre appareil. Aucune retouche, aucun filtre.
            Une empreinte cryptographique (SHA-256) est calculée pour chaque fichier — toute modification
            ultérieure sera détectée lors de la vérification.
          </div>
        </div>
      </div>

      {(() => {
        const EXPIRY_TYPES = ["driving_license", "vehicle_insurance", "rc_pro", "cargo_insurance", "technical_inspection", "fimo_fco"];
        const rows = EXPIRY_TYPES.filter((t) => byType[t]);
        if (rows.length === 0) return null;
        const todayISO = new Date().toISOString();
        const in30 = new Date(Date.now() + 30 * 864e5).toISOString();
        return (
          <div className="mb-6 rounded-md border border-[#1F4032]/15 bg-white p-4" data-testid="expiry-section">
            <div className="overline text-[#1F4032]/60 mb-1">— Échéances</div>
            <h2 className="font-display font-bold text-lg mb-1">Dates d'expiration de vos pièces</h2>
            <p className="text-xs text-neutral-600 mb-3">
              Renseignez l'expiration de vos assurances, contrôle technique et licence/permis.
              Une pièce expirée <b>bloque automatiquement</b> vos nouvelles candidatures.
            </p>
            <div className="space-y-2">
              {rows.map((t) => {
                const doc = byType[t];
                const exp = doc.expires_at || "";
                const dateVal = exp ? exp.slice(0, 10) : "";
                const expired = exp && exp < todayISO;
                const soon = exp && !expired && exp < in30;
                return (
                  <div key={t} className="flex flex-wrap items-center gap-3 justify-between border-b border-neutral-100 pb-2" data-testid={`expiry-row-${t}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{DOC_LABELS[t] || t}</div>
                      {expired ? (
                        <div className="text-xs text-red-600 font-semibold" data-testid={`expiry-expired-${t}`}>⚠ Expirée — candidatures bloquées</div>
                      ) : soon ? (
                        <div className="text-xs text-amber-600" data-testid={`expiry-soon-${t}`}>Expire bientôt</div>
                      ) : exp ? (
                        <div className="text-xs text-emerald-700">Valide</div>
                      ) : (
                        <div className="text-xs text-neutral-400">Aucune date renseignée</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        defaultValue={dateVal}
                        onChange={(e) => saveExpiry(t, e.target.value)}
                        disabled={savingExpiry === t}
                        className={`h-9 rounded-md border px-2 text-sm ${expired ? "border-red-400" : "border-input"}`}
                        data-testid={`expiry-input-${t}`}
                      />
                      {dateVal && (
                        <button type="button" onClick={() => saveExpiry(t, "")}
                          className="text-xs text-neutral-500 hover:text-[#1F4032]"
                          data-testid={`expiry-clear-${t}`}>Effacer</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      <div className="space-y-3">
        {allTypes.map((type, idx) => {
          const alwaysRequired = new Set(["selfie", "id_card", "driving_license"]);
          const required = kyc.required.includes(type) || alwaysRequired.has(type);
          const doc = byType[type];
          const showPhotoHeader = type === "id_card" &&
            allTypes.slice(0, idx).some((t) => t !== "id_card" && t !== "driving_license" && t !== "selfie");
          return (
            <React.Fragment key={type}>
              {showPhotoHeader && (
                <div className="pt-6 pb-2" data-testid="photo-section-header">
                  <div className="overline text-black mb-1">— Section photos</div>
                  <h2 className="font-display text-xl font-black tracking-tight">
                    Prenez en photo votre pièce d'identité et votre permis
                  </h2>
                  <p className="text-xs text-neutral-600 mt-1">
                    Utilisez l'appareil photo de votre téléphone (recto + verso). Aucune retouche autorisée.
                  </p>
                </div>
              )}
              <DocRow
                type={type}
                doc={doc}
                required={required}
                requiredLicense={kyc.required_license_category}
                draft={drafts[type] || {}}
                onChangeDraft={(patch) => updateDraft(type, patch)}
                onClearDraft={() => clearDraft(type)}
                onReset={() => resetDoc(doc.id, type)}
                isReady={readyDraftTypes.includes(type)}
                isIncomplete={incompleteDraftTypes.includes(type)}
              />
            </React.Fragment>
          );
        })}
      </div>

      {/* Sticky submit bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 shadow-lg z-40" data-testid="submit-all-bar">
        <div className="max-w-5xl mx-auto px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm">
            {submittingAll ? (
              <div className="flex items-center gap-2 text-[#1F4032]">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="font-mono">
                  Envoi {submitProgress.done}/{submitProgress.total}
                  {submitProgress.current && ` — ${submitProgress.current}`}
                </span>
              </div>
            ) : (
              <>
                <span className="font-display font-bold text-base" data-testid="progress-counter">
                  {progress.done}/{progress.total} documents fournis
                </span>
                {readyDraftTypes.length > 0 && (
                  <span className="ml-2 text-xs text-emerald-700 font-mono">
                    · {readyDraftTypes.length} prêt{readyDraftTypes.length > 1 ? "s" : ""} à envoyer
                  </span>
                )}
                {incompleteDraftTypes.length > 0 && (
                  <span className="ml-2 text-xs text-amber-700">
                    · {incompleteDraftTypes.length} incomplet{incompleteDraftTypes.length > 1 ? "s" : ""}
                  </span>
                )}
                {!allRequiredProvided && (
                  <div className="mt-1 text-xs text-red-600 flex items-start gap-1.5 max-w-xl" data-testid="missing-required-list">
                    <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                      À fournir avant l'envoi :{" "}
                      <span className="font-semibold">
                        {missingRequired.map((t) => DOC_LABELS[t] || t).join(", ")}
                      </span>
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
          <Button
            onClick={submitAll}
            disabled={submittingAll || !allRequiredProvided || readyDraftTypes.length === 0}
            className="rounded-full bg-[#1F4032] hover:bg-[#2A5443] text-white gap-2 px-6 h-11 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="submit-all-btn"
          >
            {submittingAll
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi en cours…</>
              : !allRequiredProvided
              ? <><Lock className="w-4 h-4" /> Complétez les pièces obligatoires</>
              : <><CheckCircle2 className="w-4 h-4" /> Valider et envoyer mes documents</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function FileSlot({ label, file, source, onPick, testIdPrefix, cameraFacing = "environment" }) {
  const cameraRef = useRef(null);
  const fileRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Crée une URL d'aperçu locale pour le fichier (image ou PDF) et la libère proprement
  useEffect(() => {
    if (!file) { setPreviewUrl(null); return; }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const isImage = file && file.type && file.type.startsWith("image/");
  const isPdf = file && file.type === "application/pdf";

  return (
    <div className="rounded-md border border-dashed border-neutral-300 p-4">
      <Label className="overline">{label}</Label>
      <div className="flex flex-wrap gap-2 mt-2">
        <input ref={cameraRef} type="file" accept="image/*" capture={cameraFacing}
          className="hidden"
          onChange={(e) => onPick(e.target.files?.[0] || null, "camera")}
          data-testid={`${testIdPrefix}-camera-input`} />
        <input ref={fileRef} type="file" accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => onPick(e.target.files?.[0] || null, "upload")}
          data-testid={`${testIdPrefix}-file-input`} />
        <Button type="button" variant="outline"
          onClick={() => cameraRef.current?.click()}
          className="rounded-full border-neutral-300 gap-2 bg-white hover:bg-neutral-100"
          data-testid={`${testIdPrefix}-camera-btn`}>
          <Camera className="w-4 h-4" /> {cameraFacing === "user" ? "Ouvrir la caméra avant" : "Prendre une photo"}
        </Button>
        <Button type="button" variant="outline"
          onClick={() => fileRef.current?.click()}
          className="rounded-full border-neutral-300 gap-2 bg-white hover:bg-neutral-100"
          data-testid={`${testIdPrefix}-upload-btn`}>
          <Upload className="w-4 h-4" /> Importer un fichier
        </Button>
      </div>
      {file && (
        <div className="mt-3 space-y-2" data-testid={`${testIdPrefix}-preview`}>
          <div className="flex items-center gap-2 text-emerald-700">
            <CheckCircle2 className="w-4 h-4" />
            <span className="text-sm font-semibold">
              {source === "camera" ? "Photo prise à l'instant" : "Fichier importé"}
            </span>
          </div>
          {isImage && previewUrl && (
            <button
              type="button"
              onClick={() => window.open(previewUrl, "_blank", "noopener,noreferrer")}
              className="block w-full rounded-md overflow-hidden border border-neutral-200 bg-neutral-50 hover:border-[#1F4032] transition-colors group relative"
              title="Cliquer pour agrandir"
              data-testid={`${testIdPrefix}-thumb`}
            >
              <img
                src={previewUrl}
                alt={file.name}
                className="w-full max-h-72 object-contain bg-white"
              />
              <span className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] font-mono px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                Cliquer pour agrandir ↗
              </span>
            </button>
          )}
          {isPdf && previewUrl && (
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 rounded-md border border-neutral-200 bg-neutral-50 hover:border-[#1F4032] hover:bg-white transition-colors"
              data-testid={`${testIdPrefix}-pdf-link`}
            >
              <FileText className="w-8 h-8 text-red-600 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm truncate">{file.name}</div>
                <div className="text-xs text-neutral-500">PDF · Cliquer pour ouvrir</div>
              </div>
            </a>
          )}
          {!isImage && !isPdf && (
            <div className="flex items-center gap-2 p-3 rounded-md border border-neutral-200 bg-neutral-50 text-sm">
              <FileText className="w-5 h-5 text-neutral-500 shrink-0" />
              <span className="truncate">{file.name}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DocRow({ type, doc, required, requiredLicense, draft, onChangeDraft, onClearDraft, onReset, isReady, isIncomplete }) {
  const [open, setOpen] = useState(false);
  const front = draft.front || null;
  const back = draft.back || null;
  const category = draft.category || doc?.license_category || requiredLicense || "B";
  const note = draft.note || "";

  const isDual = DUAL_SIDE.has(type);
  const isLicense = type === "driving_license";
  const isSelfie = type === "selfie";
  const status = doc?.status;
  const Badge = STATUS_BADGE[status];
  const licenseCategoryMismatch = isLicense && doc?.license_category &&
    !isCategoryOk(doc.license_category, requiredLicense);

  return (
    <div className="border border-neutral-200 rounded-md" data-testid={`doc-row-${type}`}>
      <div
        className="flex flex-wrap items-center justify-between gap-3 p-4 cursor-pointer hover:bg-[#1F4032]/5 transition-colors"
        onClick={() => setOpen(!open)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(!open); } }}
        data-testid={`btn-upload-${type}`}
      >
        <div className="flex items-center gap-3">
          {isDual ? <Camera className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
          <div>
            <div className="font-display font-bold flex items-center gap-2 flex-wrap">
              {DOC_LABELS[type] || type}
              {isDual && <span className="text-[10px] font-mono bg-neutral-100 px-2 py-0.5 rounded">RECTO + VERSO</span>}
              {isLicense && doc?.license_category && (
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${licenseCategoryMismatch ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                  Permis {doc.license_category}
                </span>
              )}
              {doc?.captured_from_camera && (
                <span className="text-[10px] font-mono bg-blue-100 text-blue-700 px-2 py-0.5 rounded flex items-center gap-1">
                  <Camera className="w-3 h-3" /> CAM
                </span>
              )}
            </div>
            <div className="overline mt-0.5">
              {isSelfie ? <span className="text-red-600">OBLIGATOIRE · CONTRÔLE HUMAIN</span>
                : required ? "REQUIS" : "OPTIONNEL"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Ready / Incomplete chip for draft state */}
          {isReady && (
            <span className="px-2 py-1 text-xs font-mono text-white rounded bg-emerald-600 flex items-center gap-1" data-testid={`draft-ready-${type}`}>
              <CheckCircle2 className="w-3 h-3" /> PRÊT
            </span>
          )}
          {isIncomplete && (
            <span className="px-2 py-1 text-xs font-mono text-white rounded bg-amber-500 flex items-center gap-1" data-testid={`draft-incomplete-${type}`}>
              <Clock className="w-3 h-3" /> INCOMPLET
            </span>
          )}
          {Badge && !isReady && !isIncomplete && (
            <span className={`px-2 py-1 text-xs font-mono text-white rounded ${Badge.color}`}>
              {Badge.label}
            </span>
          )}
          {/* Réinitialiser : visible quand le doc existe et n'est PAS validé. Action immédiate. */}
          {doc && doc.status !== "validated" && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onReset(); }}
              className="px-2 py-1 text-xs font-mono rounded border border-red-300 text-red-600 hover:bg-red-50 flex items-center gap-1"
              title="Supprime ce document immédiatement pour pouvoir en re-soumettre un nouveau"
              data-testid={`reset-doc-${type}`}
            >
              <RotateCcw className="w-3 h-3" /> Réinitialiser
            </button>
          )}
          <span className="text-xs font-medium text-[#1F4032] flex items-center gap-1">
            <Upload className="w-3.5 h-3.5" />
            {doc ? "Remplacer" : "Téléverser"}
          </span>
        </div>
      </div>

      {licenseCategoryMismatch && (
        <div className="px-4 pb-3 text-xs text-red-600 font-mono flex items-center gap-2">
          <X className="w-3 h-3" /> Permis {doc.license_category} insuffisant — permis {requiredLicense} requis.
        </div>
      )}
      {AI_KYC_PREANALYSIS_ENABLED && isSelfie && doc && typeof doc.face_match !== "undefined" && (
        <div className={`mx-4 mb-3 p-3 rounded-md border flex items-start gap-3 ${
          doc.face_match === true
            ? "bg-emerald-50 border-emerald-200"
            : doc.face_match === false
            ? "bg-red-50 border-red-200"
            : "bg-amber-50 border-amber-200"
        }`} data-testid="face-verification-result">
          {doc.face_match === true ? <Sparkles className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
            : doc.face_match === false ? <X className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
            : <Scan className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />}
          <div className="text-sm">
            <div className="font-semibold">
              Résultat de pré-analyse technique ({doc.face_model || "modèle configuré"}) —{" "}
              {doc.face_match === true
                ? `✓ Visage correspond à la pièce d'identité (${doc.face_confidence}% confiance)`
                : doc.face_match === false
                ? `✗ Visage différent de la pièce d'identité (${doc.face_confidence}% confiance)`
                : "Analyse impossible"}
            </div>
            {doc.face_reason && <div className="text-neutral-600 mt-1">{doc.face_reason}</div>}
          </div>
        </div>
      )}
      {doc?.rejection_reason && (
        <div className="px-4 pb-3 text-xs text-red-600 font-mono">
          Motif refus : {doc.rejection_reason}
        </div>
      )}

      {open && (
        <div className="border-t border-neutral-200 p-4 bg-neutral-50/50 space-y-4">
          {isLicense && (
            <div>
              <Label className="overline">Catégorie du permis</Label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2 max-w-2xl">
                {["B1", "B", "C1E", "C", "CE"].map((c) => (
                  <button type="button" key={c}
                    onClick={() => onChangeDraft({ category: c })}
                    className={`py-3 px-2 text-sm font-bold border rounded-md transition-colors ${category === c ? "bg-[#1F4032] text-white border-[#1F4032]" : "bg-white text-black border-neutral-300 hover:border-neutral-500"}`}
                    data-testid={`license-cat-${c}`}>
                    {c}
                    <div className="text-[10px] font-normal mt-0.5 opacity-70 leading-tight">
                      {c === "B1" ? "quadri." : c === "B" ? "≤ 3,5 t" : c === "C1E" ? "≤ 7,5 t + rem." : c === "C" ? "> 3,5 t" : "PL + rem."}
                    </div>
                  </button>
                ))}
              </div>
              {!isCategoryOk(category, requiredLicense) && (
                <p className="text-xs text-red-600 mt-2">
                  ⚠ Le permis {requiredLicense} est minimum requis pour votre véhicule.
                </p>
              )}
            </div>
          )}

          <div className={`grid ${isDual ? "md:grid-cols-2" : "md:grid-cols-1"} gap-4`}>
              <FileSlot
                label={isDual ? "Recto" : isSelfie ? "Selfie (votre visage)" : "Fichier"}
                file={front?.file}
                source={front?.source}
                onPick={(file, source) => onChangeDraft({ front: file ? { file, source } : null })}
                testIdPrefix={`front-${type}`}
                cameraFacing={isSelfie ? "user" : "environment"}
              />
              {isDual && (
                <FileSlot
                  label="Verso"
                  file={back?.file}
                  source={back?.source}
                  onPick={(file, source) => onChangeDraft({ back: file ? { file, source } : null })}
                  testIdPrefix={`back-${type}`}
                  cameraFacing="environment"
                />
              )}
          </div>

          {AI_KYC_PREANALYSIS_ENABLED && isSelfie && (
            <div className="flex items-start gap-3 p-3 rounded-md bg-blue-50 border border-blue-200 text-sm">
              <Scan className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold text-blue-900">Pré-analyse facultative assistée</div>
                <div className="text-blue-900/80 mt-1">
                  Si vous donnez votre consentement explicite, une pré-analyse peut comparer le selfie à la pièce d'identité. Elle ne prend aucune décision d'activation ; un membre habilité examine le dossier.
                  L'ordre d'envoi est géré automatiquement — votre CNI sera envoyée avant le selfie.
                </div>
              </div>
            </div>
          )}

          <div>
            <Label className="overline">Note (facultatif)</Label>
            <Input value={note} onChange={(e) => onChangeDraft({ note: e.target.value })}
              className="rounded-md mt-1 h-10" />
          </div>

          {(front || back) && (
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={onClearDraft}
                className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50 gap-2"
                data-testid={`clear-draft-${type}`}
              >
                <X className="w-4 h-4" /> Effacer ce brouillon
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
